import { Banner, Button, Dialog } from '@cloudflare/kumo';
import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useContacts } from '../hooks/useContacts';
import { useAppToast } from '../hooks/useAppToast';
import { useTenant } from '../contexts/TenantContext';
import { apiService } from '../services/api';
import { ContactList, ContactSortKey } from '../components/ContactList';
import { ContactForm } from '../components/ContactForm';
import { CategoryModal } from '../components/CategoryModal';
import { CSVImportModal } from '../components/CSVImportModal';
import { BulkEditModal } from '../components/BulkEditModal';
import { ChatwootSyncModal } from '../components/ChatwootSyncModal';
import { PerfexImportModal } from '../components/PerfexImportModal';
import { SearchAndFilters } from '../components/SearchAndFilters';
import { SortState, TablePager } from '../components/atlas/DataTableParts';
import { TagCombobox, TagCount } from '../components/atlas/TagCombobox';
import { RowActions } from '../components/atlas/RowActions';
import { Contact } from '../types';
import { DangerButton } from '../components/ui/DangerButton';
import { ICONS } from '../components/ui/icons';
import { SkeletonText } from '../components/ui/Skeleton';

// Histórico do contato: conversas da caixa de entrada
const CHANNEL_LABEL: Record<string, string> = { whatsapp: 'WhatsApp', instagram: 'Instagram', widget: 'Widget do site' };
const STATUS_LABEL: Record<string, string> = { OPEN: 'Aberta', AI: 'IA', HUMAN: 'Equipe', CLOSED: 'Encerrada' };

type BulkTagMode = 'add' | 'remove';

export function ContactsPage() {
  const toast = useAppToast();
  const { can } = useTenant();
  const canEdit = can('contacts:write');
  const [searchParams, setSearchParams] = useSearchParams();
  // ?busca= vem da busca global (Ctrl+K) e dos links da Lumi
  const [search, setSearch] = useState(() => searchParams.get('busca') || '');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [sort, setSort] = useState<SortState<ContactSortKey>>({ key: 'atualizadoEm', dir: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | undefined>();
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isCSVImportModalOpen, setIsCSVImportModalOpen] = useState(false);
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const [isChatwootSyncModalOpen, setIsChatwootSyncModalOpen] = useState(false);
  const [isPerfexImportModalOpen, setIsPerfexImportModalOpen] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [historyContact, setHistoryContact] = useState<Contact | null>(null);
  const [historyData, setHistoryData] = useState<any>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [tagOptions, setTagOptions] = useState<TagCount[]>([]);
  const [tagging, setTagging] = useState<{ contact: Contact; tags: string[] } | null>(null);
  const [bulkTags, setBulkTags] = useState<{ mode: BulkTagMode; tags: string[] } | null>(null);
  const [savingTags, setSavingTags] = useState(false);

  // Atalhos da busca global: ?busca=, ?novo=1 (novo contato), ?importar=1 (CSV)
  useEffect(() => {
    const busca = searchParams.get('busca');
    const novo = searchParams.get('novo') === '1';
    const importar = searchParams.get('importar') === '1';
    if (busca === null && !novo && !importar) return;
    if (busca !== null) { setSearch(busca); setCurrentPage(1); }
    if (novo && canEdit) { setEditingContact(undefined); setIsFormOpen(true); }
    if (importar && canEdit) setIsCSVImportModalOpen(true);
    const next = new URLSearchParams(searchParams);
    ['busca', 'novo', 'importar'].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const { contacts, total, loading, error, refresh, deleteContact } = useContacts({
    search: search || undefined,
    tag: selectedCategory || undefined,
    tags: filterTags,
    sort: sort.key,
    dir: sort.dir,
    page: currentPage,
    pageSize,
  });

  const loadTagOptions = useCallback(() => {
    apiService.getContactTags().then((r) => setTagOptions(r.tags || [])).catch(() => {});
  }, []);
  useEffect(() => { loadTagOptions(); }, [loadTagOptions]);

  const resetPage = () => setCurrentPage(1);
  const handleSearchChange = (newSearch: string) => { setSearch(newSearch); resetPage(); };
  const handleCategoryChange = (categoryId: string) => { setSelectedCategory(categoryId); resetPage(); };
  const handleTagsChange = (tags: string[]) => { setFilterTags(tags); resetPage(); };
  const handleClearFilters = () => { setSearch(''); setSelectedCategory(''); setFilterTags([]); resetPage(); };
  const handleSort = (s: SortState<ContactSortKey>) => { setSort(s); resetPage(); };
  const handleEdit = (contact: Contact) => { setEditingContact(contact); setIsFormOpen(true); };
  const handleFormSuccess = () => { setIsFormOpen(false); setEditingContact(undefined); refresh(); loadTagOptions(); };
  const handleFormCancel = () => { setIsFormOpen(false); setEditingContact(undefined); };
  const handleNewContact = () => { setEditingContact(undefined); setIsFormOpen(true); };

  // Seleção vale entre páginas (como no Atlas); o cabeçalho marca/desmarca a página atual
  const handleToggleSelectContact = (contactId: string) => {
    setSelectedContactIds((prev) => (prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]));
  };
  const handleSelectAllContacts = () => {
    const ids = contacts.map((c) => c.id);
    const all = ids.every((id) => selectedContactIds.includes(id));
    setSelectedContactIds((prev) => (all ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])]));
  };

  const handleBulkEditSuccess = () => {
    setSelectedContactIds([]);
    setIsBulkEditModalOpen(false);
    refresh();
  };

  const patchContact = async (contact: Contact, data: { nome?: string; email?: string }) => {
    await apiService.patchContact(contact.id, data);
    toast.success('Contato atualizado');
    refresh();
  };

  const saveContactTags = async () => {
    if (!tagging) return;
    setSavingTags(true);
    try {
      await apiService.patchContact(tagging.contact.id, { tags: tagging.tags });
      toast.success('Tags salvas');
      setTagging(null);
      refresh();
      loadTagOptions();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível salvar as tags');
    } finally {
      setSavingTags(false);
    }
  };

  const applyBulkTags = async () => {
    if (!bulkTags?.tags.length) return;
    setSavingTags(true);
    try {
      const r = await apiService.bulkContactTags(selectedContactIds, bulkTags.mode === 'add' ? bulkTags.tags : [], bulkTags.mode === 'remove' ? bulkTags.tags : []);
      toast.success(`${r.count} contato(s) atualizado(s)`);
      setBulkTags(null);
      refresh();
      loadTagOptions();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível alterar as tags');
    } finally {
      setSavingTags(false);
    }
  };

  const copy = async (text: string, what: string) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${what} copiado`); } catch { toast.error('Não foi possível copiar'); }
  };

  const handleViewHistory = useCallback(async (contact: Contact) => {
    setHistoryContact(contact);
    setHistoryLoading(true);
    setHistoryData(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`/api/contatos/${contact.id}/history`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) setHistoryData(await res.json());
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  if (error && contacts.length === 0) {
    return (
      <div className="p-6">
        <Banner variant="error" description={<>
          {error}{' '}
          <button onClick={refresh} className="underline hover:no-underline text-sm">
            Tentar novamente
          </button>
        </>} />
      </div>
    );
  }

  const filtering = !!(search || selectedCategory || filterTags.length);
  const selected = selectedContactIds.length;

  if (isFormOpen) {
    return <ContactForm contact={editingContact} onSuccess={handleFormSuccess} onCancel={handleFormCancel} />;
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '28px 40px 0', marginBottom: 24 }}>
        <div>
          <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Contatos</h2>
          <div style={{ color: 'var(--text-muted)' }}>
            {filtering ? `${total.toLocaleString('pt-BR')} contato(s) com os filtros` : `${total.toLocaleString('pt-BR')} contatos cadastrados`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {canEdit && <Button variant="secondary" onClick={() => setIsCategoryModalOpen(true)}>Categorias</Button>}
          {canEdit && (
            <RowActions
              label="Contatos"
              actions={[
                { label: 'Importar CSV', onClick: () => setIsCSVImportModalOpen(true) },
                { label: 'Sincronizar com o Chatwoot', onClick: () => setIsChatwootSyncModalOpen(true) },
                { label: 'Importar do Perfex CRM', onClick: () => setIsPerfexImportModalOpen(true) },
              ]}
            />
          )}
          {canEdit && <Button variant="primary" icon={ICONS.Plus} onClick={handleNewContact}>Novo contato</Button>}
        </div>
      </div>

      <div className="page-content space-y-4">
        <SearchAndFilters
          search={search}
          selectedCategory={selectedCategory}
          tags={filterTags}
          tagOptions={tagOptions}
          onSearchChange={handleSearchChange}
          onCategoryChange={handleCategoryChange}
          onTagsChange={handleTagsChange}
          onClearFilters={handleClearFilters}
        />

        {selected > 0 && (
          <div className="atlas-bulkbar" role="region" aria-label="Ações nos contatos selecionados">
            <div className="font-semibold">{selected.toLocaleString('pt-BR')} selecionado(s)</div>
            {canEdit && (
              <>
                <Button variant="secondary" icon={ICONS.Plus} onClick={() => setBulkTags({ mode: 'add', tags: [] })}>Adicionar tags</Button>
                <Button variant="secondary" icon={ICONS.Minus} onClick={() => setBulkTags({ mode: 'remove', tags: [] })}>Remover tags</Button>
                <Button variant="secondary" icon={ICONS.Edit} onClick={() => setIsBulkEditModalOpen(true)}>Categoria ou excluir</Button>
              </>
            )}
            <button type="button" className="atlas-link" onClick={() => setSelectedContactIds([])}>Limpar seleção</button>
          </div>
        )}

        <div className="bg-white rounded-lg border border-[var(--border)] overflow-hidden">
          <ContactList
            contacts={contacts}
            loading={loading}
            canEdit={canEdit}
            sort={sort}
            onSort={handleSort}
            onEdit={handleEdit}
            onDelete={deleteContact}
            onManageTags={(contact) => setTagging({ contact, tags: contact.tags || [] })}
            onPatch={patchContact}
            onViewHistory={handleViewHistory}
            onCopy={copy}
            selectedContactIds={selectedContactIds}
            onToggleSelect={handleToggleSelectContact}
            onSelectAll={handleSelectAllContacts}
            emptyMessage={filtering ? 'Nenhum contato com esses filtros.' : undefined}
          />
          {total > 0 && (
            <TablePager page={currentPage} pageSize={pageSize} total={total} onPage={setCurrentPage} onPageSize={setPageSize} />
          )}
        </div>
      </div>

      <Dialog.Root open={tagging !== null} onOpenChange={(o: boolean) => { if (!o) (() => setTagging(null))(); }}>
<Dialog size="sm" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Gerenciar tags</h3>}</Dialog.Title>

        <div style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
          Tags de <strong>{tagging?.contact.nome}</strong>. Busque uma tag existente ou digite para criar.
        </div>
        {tagging && (
          <TagCombobox label="Tags" allowCreate closeOnSelect value={tagging.tags} options={tagOptions} onChange={(tags) => setTagging({ ...tagging, tags })} />
        )}
        <div className="flex justify-end gap-2" style={{ marginTop: 24 }}>
          <Button variant="secondary" type="button" onClick={() => setTagging(null)}>Cancelar</Button>
          <Button variant="primary" loading={savingTags} onClick={saveContactTags}>Salvar</Button>
        </div>
      
</Dialog>
</Dialog.Root>

      <Dialog.Root open={bulkTags !== null} onOpenChange={(o: boolean) => { if (!o) (() => setBulkTags(null))(); }}>
<Dialog size="sm" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">{bulkTags?.mode === 'remove' ? 'Remover tags' : 'Adicionar tags'}</h3>}</Dialog.Title>

        <div style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
          {bulkTags?.mode === 'remove'
            ? `As tags escolhidas saem dos ${selected} contato(s) selecionado(s). As outras tags ficam.`
            : `As tags escolhidas entram nos ${selected} contato(s) selecionado(s), sem apagar as que eles já têm.`}
        </div>
        {bulkTags && (
          <TagCombobox label="Tags" allowCreate={bulkTags.mode === 'add'} closeOnSelect value={bulkTags.tags} options={tagOptions} onChange={(tags) => setBulkTags({ ...bulkTags, tags })} />
        )}
        <div className="flex justify-end gap-2" style={{ marginTop: 24 }}>
          <Button variant="secondary" type="button" onClick={() => setBulkTags(null)}>Cancelar</Button>
          {bulkTags?.mode === 'remove' ? (
            <DangerButton loading={savingTags} disabled={!bulkTags.tags.length} onClick={applyBulkTags}>Remover</DangerButton>
          ) : (
            <Button variant="primary" loading={savingTags} disabled={!bulkTags?.tags.length} onClick={applyBulkTags}>Adicionar</Button>
          )}
        </div>
      
</Dialog>
</Dialog.Root>


      <CategoryModal isOpen={isCategoryModalOpen} onClose={() => setIsCategoryModalOpen(false)} />
      <CSVImportModal
        isOpen={isCSVImportModalOpen}
        onClose={() => setIsCSVImportModalOpen(false)}
        onSuccess={() => { refresh(); loadTagOptions(); setIsCSVImportModalOpen(false); }}
      />
      <BulkEditModal
        isOpen={isBulkEditModalOpen}
        onClose={() => setIsBulkEditModalOpen(false)}
        selectedContactIds={selectedContactIds}
        onSuccess={handleBulkEditSuccess}
      />
      <ChatwootSyncModal
        isOpen={isChatwootSyncModalOpen}
        onClose={() => setIsChatwootSyncModalOpen(false)}
        onSuccess={() => { refresh(); setIsChatwootSyncModalOpen(false); }}
      />
      <PerfexImportModal
        isOpen={isPerfexImportModalOpen}
        onClose={() => setIsPerfexImportModalOpen(false)}
        onSuccess={() => { refresh(); setIsPerfexImportModalOpen(false); }}
      />

      {/* Modal de Histórico de Contato */}
      <Dialog.Root open={!!historyContact} onOpenChange={(o: boolean) => { if (!o) (() => { setHistoryContact(null); setHistoryData(null); })(); }}>
<Dialog size="lg" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Histórico de {historyContact?.nome}</h3>}</Dialog.Title>

        <div className="space-y-4">
          {historyLoading && (
            <div className="flex justify-center py-8">
              <SkeletonText lines={3} className="w-full" />
            </div>
          )}
          {!historyLoading && historyData && (
            <>
              {historyData.conversations.length === 0 ? (
                <div className="text-gray-500 text-center py-8">Nenhuma conversa com este contato ainda.</div>
              ) : (
                <div className="overflow-auto max-h-[60vh]">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Canal</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Atendimento</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Última mensagem</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quando</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {historyData.conversations.map((c: { id: string; channel: string; status: string; lastPreview: string | null; lastMessageAt: string }) => (
                        <tr key={c.id}>
                          <td className="px-4 py-2 text-gray-900">{CHANNEL_LABEL[c.channel] ?? c.channel}</td>
                          <td className="px-4 py-2 text-gray-600">{STATUS_LABEL[c.status] ?? c.status}</td>
                          <td className="px-4 py-2 text-gray-600">{c.lastPreview || '—'}</td>
                          <td className="px-4 py-2 text-gray-600">{new Date(c.lastMessageAt).toLocaleString('pt-BR')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      
</Dialog>
</Dialog.Root>
    </>
  );
}
